import {
  Navigate,
  Route,
  Routes,
} from "react-router-dom";


/*
|--------------------------------------------------------------------------
| AUTH PAGES
|--------------------------------------------------------------------------
*/

import LoginPage from "../modules/auth/pages/LoginPage";
import UsersPage from "../modules/auth/pages/UsersPage";
import CreateUserPage from "../modules/auth/pages/CreateUserPage";
import EditUserPage from "../modules/auth/pages/EditUserPage";
import UserDetailsPage from "../modules/auth/pages/UserDetailsPage";


/*
|--------------------------------------------------------------------------
| CATEGORY PAGES
|--------------------------------------------------------------------------
*/

import CategoriesPage from "../modules/categories/pages/CategoriesPage";
import CategoryDetailsPage from "../modules/categories/pages/CategoryDetailsPage";
import CreateCategoryPage from "../modules/categories/pages/CreateCategoryPage";
import EditCategoryPage from "../modules/categories/pages/EditCategoryPage";


/*
|--------------------------------------------------------------------------
| OFFERING PAGES
|--------------------------------------------------------------------------
*/

import OfferingsPage from "../modules/offerings/pages/OfferingsPage";
import OfferingCreatePage from "../modules/offerings/pages/OfferingCreatePage";
import OfferingEditPage from "../modules/offerings/pages/OfferingEditPage";
import OfferingDetailsPage from "../modules/offerings/pages/OfferingDetailsPage";


/*
|--------------------------------------------------------------------------
| OFFER PAGES
|--------------------------------------------------------------------------
*/

import OffersPage from "../modules/offers/pages/OffersPage";
import CreateOfferPage from "../modules/offers/pages/CreateOfferPage";
import EditOfferPage from "../modules/offers/pages/EditOfferPage";
import OfferDetailsPage from "../modules/offers/pages/OfferDetailsPage";


/*
|--------------------------------------------------------------------------
| QUERY PAGES
|--------------------------------------------------------------------------
*/

import QueriesPage from "../modules/queries/pages/QueriesPage";
import QueryDetailsPage from "../modules/queries/pages/QueryDetailsPage";


/*
|--------------------------------------------------------------------------
| PAYMENT PAGES
|--------------------------------------------------------------------------
*/

import PaymentsPage from "../modules/payments/pages/PaymentsPage";
import PaymentDetailsPage from "../modules/payments/pages/PaymentDetailsPage";


/*
|--------------------------------------------------------------------------
| ROUTE GUARDS
|--------------------------------------------------------------------------
*/

import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import RoleRoute from "./RoleRoute";


/*
|--------------------------------------------------------------------------
| LAYOUT
|--------------------------------------------------------------------------
*/

import DashboardLayout from "../components/layout/DashboardLayout";


/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

import {
  ROLES,
} from "../modules/auth/constants/auth.constants";


const AppRoutes = () => {
  return (
    <Routes>

      {/*
      |--------------------------------------------------------------------------
      | PUBLIC ROUTES
      |--------------------------------------------------------------------------
      */}

      <Route element={<PublicRoute />}>

        <Route
          path="/login"
          element={<LoginPage />}
        />

      </Route>


      {/*
      |--------------------------------------------------------------------------
      | PROTECTED ROUTES
      |--------------------------------------------------------------------------
      |
      | Every route inside this section requires authentication.
      |
      */}

      <Route element={<ProtectedRoute />}>

        {/*
        |--------------------------------------------------------------------------
        | DASHBOARD LAYOUT
        |--------------------------------------------------------------------------
        |
        | Sidebar + Header + page content.
        |
        */}

        <Route element={<DashboardLayout />}>

          {/*
          |--------------------------------------------------------------------------
          | DEFAULT AUTHENTICATED ROUTE
          |--------------------------------------------------------------------------
          */}

          <Route
            path="/"
            element={
              <Navigate
                to="/categories"
                replace
              />
            }
          />


          {/*
          |--------------------------------------------------------------------------
          | USER MANAGEMENT
          |--------------------------------------------------------------------------
          |
          | User management is ADMIN-only.
          |
          */}

          <Route
            element={
              <RoleRoute
                allowedRoles={[
                  ROLES.ADMIN,
                ]}
              />
            }
          >

            <Route
              path="/users"
              element={<UsersPage />}
            />

            <Route
              path="/users/create"
              element={<CreateUserPage />}
            />

            <Route
              path="/users/:id"
              element={<UserDetailsPage />}
            />

            <Route
              path="/users/:id/edit"
              element={<EditUserPage />}
            />

          </Route>


          {/*
          |--------------------------------------------------------------------------
          | CATEGORY MANAGEMENT
          |--------------------------------------------------------------------------
          |
          | LIST
          | ----
          | All authenticated users can access the category list.
          |
          | DETAILS
          | -------
          | All authenticated users can view category details.
          |
          | CREATE
          | ------
          | Backend POST /categories requires ADMIN.
          |
          | EDIT
          | ----
          | Backend PATCH /categories/:id allows authenticated users.
          |
          */}

          <Route
            path="/categories"
            element={<CategoriesPage />}
          />

          <Route
            path="/categories/:id"
            element={<CategoryDetailsPage />}
          />


          <Route
            element={
              <RoleRoute
                allowedRoles={[
                  ROLES.ADMIN,
                ]}
              />
            }
          >

            <Route
              path="/categories/create"
              element={<CreateCategoryPage />}
            />

          </Route>


          <Route
            path="/categories/:id/edit"
            element={<EditCategoryPage />}
          />


          {/*
          |--------------------------------------------------------------------------
          | QUERY MANAGEMENT
          |--------------------------------------------------------------------------
          |
          | GET /queries
          | GET /queries/:id
          |
          | These routes are already inside the main
          | ProtectedRoute above.
          |
          | Therefore we do NOT add another ProtectedRoute here.
          |
          */}

          <Route
            path="/queries"
            element={<QueriesPage />}
          />

          <Route
            path="/queries/:id"
            element={<QueryDetailsPage />}
          />


          {/*
          |--------------------------------------------------------------------------
          | OFFERING MANAGEMENT
          |--------------------------------------------------------------------------
          |
          | LIST
          | ----
          | All authenticated users can access the offering list.
          |
          | CREATE
          | ------
          | ADMIN only.
          |
          | DETAILS
          | -------
          | All authenticated users can view offering details.
          |
          | EDIT
          | ----
          | ADMIN only.
          |
          */}


          {/*
          |--------------------------------------------------------------------------
          | OFFERING LIST
          |--------------------------------------------------------------------------
          |
          | GET /offerings
          |
          */}

          <Route
            path="/offerings"
            element={<OfferingsPage />}
          />


          {/*
          |--------------------------------------------------------------------------
          | OFFERING CREATE
          |--------------------------------------------------------------------------
          |
          | POST /offerings
          |
          | ADMIN only.
          |
          */}

          <Route
            element={
              <RoleRoute
                allowedRoles={[
                  ROLES.ADMIN,
                ]}
              />
            }
          >

            <Route
              path="/offerings/create"
              element={<OfferingCreatePage />}
            />

          </Route>


          {/*
          |--------------------------------------------------------------------------
          | OFFERING DETAILS
          |--------------------------------------------------------------------------
          |
          | GET /offerings/:id
          |
          | Must remain outside the ADMIN guard because
          | authenticated users can view offerings.
          |
          */}

          <Route
            path="/offerings/:id"
            element={<OfferingDetailsPage />}
          />


          {/*
          |--------------------------------------------------------------------------
          | OFFERING EDIT
          |--------------------------------------------------------------------------
          |
          | PATCH /offerings/:id
          |
          | ADMIN only.
          |
          */}

          <Route
            element={
              <RoleRoute
                allowedRoles={[
                  ROLES.ADMIN,
                ]}
              />
            }
          >

            <Route
              path="/offerings/:id/edit"
              element={<OfferingEditPage />}
            />

          </Route>


          {/*
          |--------------------------------------------------------------------------
          | OFFER MANAGEMENT
          |--------------------------------------------------------------------------
          |
          | LIST
          | ----
          | All authenticated users can access the offer list.
          |
          | CREATE
          | ------
          | ADMIN only.
          |
          | DETAILS
          | -------
          | All authenticated users can view offer details.
          |
          | EDIT
          | ----
          | ADMIN only.
          |
          */}


          {/*
          |--------------------------------------------------------------------------
          | OFFER LIST
          |--------------------------------------------------------------------------
          |
          | GET /offers
          |
          */}

          <Route
            path="/offers"
            element={<OffersPage />}
          />


          {/*
          |--------------------------------------------------------------------------
          | OFFER CREATE
          |--------------------------------------------------------------------------
          |
          | POST /offers
          |
          | ADMIN only.
          |
          */}

          <Route
            element={
              <RoleRoute
                allowedRoles={[
                  ROLES.ADMIN,
                ]}
              />
            }
          >

            <Route
              path="/offers/create"
              element={<CreateOfferPage />}
            />

          </Route>


          {/*
          |--------------------------------------------------------------------------
          | OFFER DETAILS
          |--------------------------------------------------------------------------
          |
          | GET /offers/:id
          |
          | Must remain outside the ADMIN guard because
          | authenticated users can view offer details.
          |
          */}

          <Route
            path="/offers/:id"
            element={<OfferDetailsPage />}
          />


          {/*
          |--------------------------------------------------------------------------
          | OFFER EDIT
          |--------------------------------------------------------------------------
          |
          | PATCH /offers/:id
          |
          | ADMIN only.
          |
          */}

          <Route
            element={
              <RoleRoute
                allowedRoles={[
                  ROLES.ADMIN,
                ]}
              />
            }
          >

            <Route
              path="/offers/:id/edit"
              element={<EditOfferPage />}
            />

          </Route>


          {/*
          |--------------------------------------------------------------------------
          | PAYMENT MANAGEMENT
          |--------------------------------------------------------------------------
          |
          | LIST
          | ----
          | All authenticated users can access the payment list.
          |
          | DETAILS
          | -------
          | All authenticated users can view payment details.
          |
          | Payment creation/checkout is handled through
          | the offer/payment flow and does not require
          | a separate create payment page here.
          |
          */}


          {/*
          |--------------------------------------------------------------------------
          | PAYMENT LIST
          |--------------------------------------------------------------------------
          |
          | GET /payments
          |
          */}

          <Route
            path="/payments"
            element={<PaymentsPage />}
          />


          {/*
          |--------------------------------------------------------------------------
          | PAYMENT DETAILS
          |--------------------------------------------------------------------------
          |
          | GET /payments/:id
          |
          */}

          <Route
            path="/payments/:id"
            element={<PaymentDetailsPage />}
          />


        </Route>

      </Route>


      {/*
      |--------------------------------------------------------------------------
      | UNKNOWN ROUTE
      |--------------------------------------------------------------------------
      |
      | Send unknown URLs to the appropriate starting page.
      |
      */}

      <Route
        path="*"
        element={
          <Navigate
            to="/"
            replace
          />
        }
      />

    </Routes>
  );
};


export default AppRoutes;
